/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { CardMetadata, Grid, ProductCard, Typography2 } from '@coursera/cds-core';

import { useTracker, useVisibilityTracker } from 'bundles/page/lib/event-pulse/react';
import { useEventingData } from 'bundles/unified-career-academy/contexts/EventingContext';
import type { ArticleCardFieldsFragment } from 'bundles/unified-seo-content-common/__generated__/contentfulTypes';
import { PAGE_TYPE } from 'bundles/unified-seo-content-common/constants';
import useEstimatedReadTimeData from 'bundles/unified-seo-content-common/hooks/useEstimatedReadTimeData';
import { getPageLink } from 'bundles/unified-seo-content-common/utils';

import _t from 'i18n!nls/career-academy';

export type Props = {
  article: ArticleCardFieldsFragment;
  slug: string;
};

export const RolePageCareerResourceCard = ({ article, slug }: Props) => {
  const eventingData = useEventingData();
  const track = useTracker();
  const { estimatedReadTimeLabel } = useEstimatedReadTimeData({ slug });
  const metadata = [_t('Article')];
  if (estimatedReadTimeLabel) {
    metadata.push(estimatedReadTimeLabel);
  }

  const articleCardEventingV3Data = {
    article: {
      articleSlug: slug,
      articleTitle: article.title ?? undefined,
    },
    ...eventingData,
  };

  const articleCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_article_card',
    articleCardEventingV3Data
  );

  const trackClick = () => {
    track('click_article_card', articleCardEventingV3Data);
  };

  return (
    <Grid item xs={6} md={3} key={slug}>
      <ProductCard
        ref={articleCardRef}
        variant="grid"
        productType="Article"
        previewImageSrc={article.featuredImage?.url || ''}
        title={{
          name: article.title || _t('Coursera Article'),
          linkProps: {
            href: getPageLink({
              slug,
              localizedCountryCode: article.localizedCountryCode,
              pageType: PAGE_TYPE.content,
            }),
            target: '_blank',
            onClick: trackClick,
          },
        }}
        body={
          <Typography2 variant="bodySecondary" color="supportText" component="p">
            {article.intro || _t('This is a Coursera Article')}
          </Typography2>
        }
        footer={
          <>
            <br />
            <CardMetadata metadata={metadata} />
          </>
        }
      />
    </Grid>
  );
};
